import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  LinearProgress,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import { useOfferInventoryContext } from '~/hooks/apis/stopsell/useOfferInventoryContext';

import {
  INVENTORY_DECISION_LABELS,
  InventoryDecisionResult,
  InventoryWizardAnswers,
  OfferInventoryContext,
  getDecisionSeverity,
} from '~/types/inventoryWizard';

import { computeInventoryDecision } from '~/utils/inventoryWizardDecision';

type QuestionKey = keyof InventoryWizardAnswers;

interface Props {
  open: boolean;
  onClose: () => void;
  onDecision: (result: InventoryDecisionResult) => void;
  offers: Array<App.Offer>;
}

const QUESTION_LABELS: Record<QuestionKey, string> = {
  recent_go_live: 'Did the campaign go live within the last 3 business days?',
  tier_1_or_2: 'Is this a Tier 1 or Tier 2 campaign?',
  campaign_live: 'Is the campaign currently live (open for bookings)?',
  request_full_month_or_over_50pct_calendar:
    'Does the request cover a full calendar month, or more than 50% of the campaign calendar?',
  request_over_50pct_month_within_next_3_months:
    'Does the request cover more than 50% of a calendar month within the next 3 months?',
  at_least_30_days_availability_remaining: 'Is there at least 30 days of availability remaining across the campaign?',
  has_bnbls: 'Are there any BNBLs (Book Now, Book Later) on this campaign?',
};

const STEP_LABELS: Record<QuestionKey, string> = {
  recent_go_live: 'Go-live',
  tier_1_or_2: 'Campaign tier',
  campaign_live: 'Live now',
  request_full_month_or_over_50pct_calendar: 'Request size',
  request_over_50pct_month_within_next_3_months: 'Request size',
  at_least_30_days_availability_remaining: 'Availability',
  has_bnbls: 'BNBLs',
};

const PRE_FILLABLE: Set<QuestionKey> = new Set(['recent_go_live', 'campaign_live', 'tier_1_or_2', 'has_bnbls']);

function makePrefilled(ctx: OfferInventoryContext): Partial<InventoryWizardAnswers> {
  const next: Partial<InventoryWizardAnswers> = {};
  for (const field of PRE_FILLABLE) {
    const value = ctx[field as keyof OfferInventoryContext];
    if (value !== null) next[field as keyof InventoryWizardAnswers] = value as boolean;
  }
  return next;
}

function getVisibleQuestions(answers: Partial<InventoryWizardAnswers>): Array<QuestionKey> {
  const questions: Array<QuestionKey> = ['recent_go_live'];

  if (answers.recent_go_live === undefined) return questions;
  if (answers.recent_go_live === true) return questions;

  questions.push('tier_1_or_2', 'campaign_live');

  if (answers.tier_1_or_2 === undefined || answers.campaign_live === undefined) return questions;

  if (answers.tier_1_or_2) {
    if (answers.campaign_live) {
      questions.push('request_over_50pct_month_within_next_3_months');
    } else {
      questions.push('request_full_month_or_over_50pct_calendar');
      if (answers.request_full_month_or_over_50pct_calendar === true) {
        questions.push('at_least_30_days_availability_remaining');
        if (answers.at_least_30_days_availability_remaining === false) {
          questions.push('has_bnbls');
        }
      }
    }
  } else if (answers.campaign_live) {
    questions.push('request_full_month_or_over_50pct_calendar');
  }

  return questions;
}

function isDecisionReady(answers: Partial<InventoryWizardAnswers>): boolean {
  const visible = getVisibleQuestions(answers);
  const last = visible[visible.length - 1];
  return answers[last] !== undefined;
}

export default function InventoryWizard({ open, onClose, onDecision, offers }: Props) {
  const [selectedOfferId, setSelectedOfferId] = useState<string>('');
  const [answers, setAnswers] = useState<Partial<InventoryWizardAnswers>>({});
  const [activeStep, setActiveStep] = useState(0);

  const {
    isLoading: isContextLoading,
    isError: isContextError,
    ...offerContext
  } = useOfferInventoryContext(selectedOfferId || null);

  const offerMenuItems = useMemo(
    () =>
      offers.map((o) => (
        <MenuItem key={o.id_salesforce_external} value={o.id_salesforce_external}>
          {o.name || o.id_salesforce_external}
        </MenuItem>
      )),
    [offers],
  );

  useEffect(() => {
    if (open) {
      const defaultId = offers[0]?.id_salesforce_external ?? '';
      setActiveStep(0);
      if (selectedOfferId === defaultId) {
        setAnswers(makePrefilled(offerContext));
      } else {
        setSelectedOfferId(defaultId);
        setAnswers({});
      }
    }
    // offerContext and selectedOfferId intentionally omitted: we don't want this to re-run when
    // data loads mid-flow; the separate offerContext effect handles async arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, offers]);

  useEffect(() => {
    setAnswers((prev) => {
      const next = { ...prev };
      for (const field of PRE_FILLABLE) {
        const value = offerContext[field as keyof OfferInventoryContext];
        // Only fill fields the user hasn't answered yet — avoids overwriting an explicit override
        // if the API responds after the user has already started the wizard.
        if (value !== null && prev[field as keyof InventoryWizardAnswers] === undefined) {
          next[field as keyof InventoryWizardAnswers] = value as boolean;
        }
      }
      return next;
    });
    // No step reset here: the open effect handles it on wizard open, and handleOfferChange
    // handles it on offer switch. Resetting here would send users back to step 0 mid-flow
    // if the API responds after they've already advanced.
  }, [
    selectedOfferId,
    offerContext.recent_go_live,
    offerContext.campaign_live,
    offerContext.tier_1_or_2,
    offerContext.has_bnbls,
  ]);

  const visibleQuestions = useMemo(() => getVisibleQuestions(answers), [answers]);
  const ready = useMemo(() => isDecisionReady(answers), [answers]);
  const onResultStep = ready && activeStep >= visibleQuestions.length;

  const currentQuestion = visibleQuestions[activeStep];
  const isCurrentPreFilled =
    currentQuestion !== undefined &&
    PRE_FILLABLE.has(currentQuestion) &&
    !isContextLoading &&
    offerContext[currentQuestion as keyof OfferInventoryContext] !== null;

  const decisionResult = useMemo<InventoryDecisionResult | null>(() => {
    if (!ready) return null;
    const { recent_go_live, tier_1_or_2, campaign_live } = answers;
    if (recent_go_live !== true && (tier_1_or_2 === undefined || campaign_live === undefined)) return null;
    return computeInventoryDecision(answers as InventoryWizardAnswers);
  }, [ready, answers]);

  const handleAnswer = useCallback((key: QuestionKey, value: boolean) => {
    setAnswers((prev) => {
      const visible = getVisibleQuestions(prev);
      const idx = visible.indexOf(key);
      const next = { ...prev };
      for (const q of visible.slice(idx + 1)) {
        delete next[q];
      }
      next[key] = value;
      return next;
    });
    setActiveStep((s) => s + 1);
  }, []);

  const handleBack = useCallback(() => {
    setActiveStep((s) => Math.max(0, s - 1));
  }, []);

  const handleContinue = useCallback(() => {
    setActiveStep((s) => s + 1);
  }, []);

  const handleReset = useCallback(() => {
    setAnswers(makePrefilled(offerContext));
    setActiveStep(0);
  }, [offerContext]);

  const handleOfferChange = useCallback((e: SelectChangeEvent) => {
    setSelectedOfferId(e.target.value);
    setAnswers({});
    setActiveStep(0);
  }, []);

  const handleProceed = useCallback(() => {
    if (decisionResult) {
      onDecision(decisionResult);
      onClose();
    }
  }, [decisionResult, onDecision, onClose]);

  const handleCloseWithDecision = useCallback(() => {
    if (decisionResult) {
      onDecision(decisionResult);
    }
    onClose();
  }, [decisionResult, onDecision, onClose]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h5" fontWeight={600} component="span">
          Inventory Assessment
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          Answer a few questions to determine the best course of action.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          <FormControl fullWidth size="small">
            <FormLabel sx={{ mb: 0.5 }}>Offer</FormLabel>
            <Select value={selectedOfferId} onChange={handleOfferChange} displayEmpty>
              {offerMenuItems}
            </Select>
          </FormControl>

          {isContextError && selectedOfferId && (
            <Alert severity="warning">Could not load offer data. Please answer all questions manually.</Alert>
          )}

          {!onResultStep && currentQuestion ? (
            <Box
              sx={{
                bgcolor: 'grey.50',
                border: '1px solid',
                borderColor: 'grey.200',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
              }}
            >
              <Typography variant="h6" fontWeight={500} mb={4}>
                {QUESTION_LABELS[currentQuestion]}
              </Typography>
              {isContextLoading && PRE_FILLABLE.has(currentQuestion) ? (
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Loading offer data...
                  </Typography>
                </Stack>
              ) : (
                <>
                  <ToggleButtonGroup
                    value={answers[currentQuestion] !== undefined ? (answers[currentQuestion] ? 'yes' : 'no') : null}
                    exclusive
                    onChange={(_, value) => {
                      if (value !== null) {
                        handleAnswer(currentQuestion, value === 'yes');
                      } else if (answers[currentQuestion] !== undefined) {
                        // User re-clicked the already-selected button — advance without changing the answer
                        handleContinue();
                      }
                    }}
                    sx={{ '& .MuiToggleButton-root.Mui-selected': { fontWeight: 700 } }}
                  >
                    <ToggleButton value="yes" color="success" sx={{ minWidth: 140, py: 1.5, fontSize: '1rem' }}>
                      Yes
                    </ToggleButton>
                    <ToggleButton value="no" color="error" sx={{ minWidth: 140, py: 1.5, fontSize: '1rem' }}>
                      No
                    </ToggleButton>
                  </ToggleButtonGroup>
                  {isCurrentPreFilled && (
                    <Typography variant="caption" color="text.secondary" display="block" mt={2}>
                      Auto-filled from offer — you can change this if needed
                    </Typography>
                  )}
                </>
              )}
            </Box>
          ) : (
            decisionResult && (
              <Alert severity={getDecisionSeverity(decisionResult.decision)} sx={{ py: 2 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {INVENTORY_DECISION_LABELS[decisionResult.decision]}
                </Typography>
                <Typography variant="body2" mt={0.5}>
                  {decisionResult.reason}
                </Typography>
              </Alert>
            )
          )}
          <Box>
            <Stack direction="row" justifyContent="space-between" mb={1}>
              <Typography variant="caption" color="text.secondary">
                Step {Math.min(activeStep + 1, visibleQuestions.length + 1)} of {visibleQuestions.length + 1}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {onResultStep ? 'Decision' : STEP_LABELS[currentQuestion]}
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={onResultStep ? 100 : (activeStep / visibleQuestions.length) * 100}
            />
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Box>
          {activeStep > 0 && (
            <Button variant="text" onClick={handleBack}>
              Back
            </Button>
          )}
        </Box>
        <Box>
          <Button variant="text" onClick={handleReset}>
            Reset
          </Button>
          <Button variant="text" onClick={onClose}>
            Cancel
          </Button>
          {isCurrentPreFilled && !onResultStep && (
            <Button variant="contained" onClick={handleContinue}>
              Continue
            </Button>
          )}
          {onResultStep && decisionResult?.decision === 'UPDATE_INVENTORY' && (
            <Button variant="contained" onClick={handleProceed}>
              Proceed
            </Button>
          )}
          {onResultStep && decisionResult?.decision !== 'UPDATE_INVENTORY' && (
            <Button variant="contained" onClick={handleCloseWithDecision}>
              Close
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
}
